import {IImportInterWspSvc, ITEM, OImportInterWspOptions} from "/@lib@/wsp/item.js";
import {IWspUiEnv, WSP, Wsp} from "/@lib@/wsp/wsp.js";
import {ESrcSt, JSrcFields, SRC} from "/@lib@/wsp/src.js";
import {IReg} from "/@lib@/commons/registry.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {SrcNewCode} from "/@back@/wsp/dialogs/srcNewCode.js";
import {IO} from "/@lib@/commons/io/io.js";
import {DOM, JSX, ShadowJsx} from "/@lib@/commons/xml/dom.js";
import {OSrcDrawerInit} from "/@back@/wsp/widgets/srcDrawer.js";
import {Button} from "/@back@/commons/widgets/buttons.js";
import "/@lib@/wsp/wsp_Perms.js";

export class WspImportInterWsp implements IImportInterWspSvc {

	protected transientCache: JSrcFields;

	async importSrc(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		this.transientCache = null; //reset
		const canPrx = this.canPrx(wspFrom, srcFrom, options, reg, uiCtx);
		const canCopy = this.canCopy(wspFrom, srcFrom, options, reg, uiCtx);
		const canExt = this.canExt(wspFrom, srcFrom, options, reg, uiCtx);
		const countChoices = ((canCopy ? 1 : 0) + (canExt ? 1 : 0) + (canPrx ? 1 : 0));

		//Actuellement, la compat est déjà évaluée en amont, mais c'est imparfait (modèles doc !=).
		//if (countChoices > 0 && !await this.isTargetCompat(wspFrom, srcFrom, options, reg, uiCtx)) return null;

		if (countChoices > 1) {
			const ct = <form id="main"/> as HTMLFormElement;
			if (canExt) {
				ct.appendChild(<label><input name="choice" value="ext" type="radio"/>Lier directement cet item étranger</label>)
			}
			if (canPrx) {
				const transient = await this.getTransient(wspFrom, srcFrom, options, reg, uiCtx);
				if (!transient.srcId) {
					//Prx pas encore existant
					ct.appendChild(<label class="ctrlLbl"><input name="choice" value="prx" type="radio"/>Créer une réplique locale de cet item étranger</label>)
				} else if (options.refExtItemAcceptable) {
					ct.appendChild(<label class="ctrlLbl"><input name="choice" value="prx" type="radio"/>Lier la réplique locale (déjà créée) de cet item étranger</label>)
				} else if (options.uriParentFrozen && ITEM.isAirItem(transient.srcUri)) {
					ct.appendChild(<label class="ctrlLbl"><input name="choice" value="prx" type="radio"/>Ancrer dans cet espace la réplique locale (déjà créée) de cet item étranger</label>)
				} else {
					//prx déjà ancré, option désactivée
					const uri = ITEM.getItemUriLabel(transient.srcUri);
					ct.appendChild(<label>
						<input name="choice" value="prx" disabled="" type="radio"/>{`Une réplique de cet item étranger existe déjà :`}
						<wsp-src-drawer-inline î={{reg, shortDesc: transient} as OSrcDrawerInit}/>
					</label>);
				}
			}
			if (canCopy) {
				ct.appendChild(<label><input name="choice" value="copy" type="radio"/>Dupliquer ce contenu étranger dans l'atelier</label>)
			}

			const firstOpt = DOM.findNext(ct, ct, (elt): elt is HTMLInputElement => elt instanceof HTMLInputElement && !elt.disabled);
			if (firstOpt) firstOpt.checked = true;

			const result = await POPUP.showDialog<'ext' | 'prx' | 'copy'>(<c-box skinOver="webzone:panel form-control-areas standard-dialog" onkeypress={function onKeypress(this: HTMLElement, ev: KeyboardEvent) {
				if (ev.key === 'Enter') {
					ev.preventDefault();
					ev.stopImmediatePropagation();
					this.shadowRoot.getElementById("ok").click();
				}
			}}>
				<ShadowJsx>
					{ct}
					<div id="footer">
						<c-button id="ok" label="Valider" class="default" ui-context="dialog" onclick={function (this: Button) {POPUP.findPopupableParent(this).close((ct.elements.namedItem("choice") as RadioNodeList).value)}}/>
						<c-button label="Annuler" ui-context="dialog" onclick={function (this: Button) {POPUP.findPopupableParent(this).close(null)}}/>
					</div>
				</ShadowJsx>
			</c-box>, uiCtx, {titleBar: "Contenu issu d'un autre atelier..."}).onNextClose();
			switch (result) {
			case "ext":
				return reg.env.wsp.fetchShortDesc(ITEM.buildTansientUri(wspFrom.code, ITEM.srcUriSub(srcFrom)), uiCtx);
			case "prx":
				return this.doPrx(wspFrom, srcFrom, options, reg, uiCtx);
			case "copy":
				return this.doCopy(wspFrom, srcFrom, options, reg, uiCtx);
			}
			return null;
		} else if (canExt) {
			return reg.env.wsp.fetchShortDesc(ITEM.buildTansientUri(wspFrom.code, ITEM.srcUriSub(srcFrom)), uiCtx);
		} else if (canCopy) {
			return this.doCopy(wspFrom, srcFrom, options, reg, uiCtx);
		} else if (canPrx) {
			return this.doPrx(wspFrom, srcFrom, options, reg, uiCtx);
		} else if (options.refExtItemAcceptable) {
			if (!wspFrom.isPublic) {
				POPUP.showNotifForbidden("L'atelier auquel appartiennent les items issus du presse-papier n'est pas déclaré 'public', les items ne peuvent pas être directement référencés.", uiCtx);
			} else if (reg.env.wsp.isExtItem) {
				POPUP.showNotifForbidden("Cet atelier n'est pas configuré pour pouvoir référencer des items issus d'autres ateliers.", uiCtx);
			} else {
				POPUP.showNotifForbidden("Impossible de copier des contenus issus d'un autre atelier.", uiCtx);
			}
		} else {
			POPUP.showNotifForbidden("Impossible de copier des contenus issus d'un autre atelier.", uiCtx);
		}
		return null
	}

	/**
	 * Retourne srcFrom, idéalement traduit dans le contexte de l'atelier cible, sans être encore réellement importé.
	 * Ok en DB, en FS, c'est imparfait, on retourne simplement srcFrom.
	 */
	protected async getTransient(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields> {
		if (!this.transientCache) {
			this.transientCache = reg.env.universe.config.backEnd === "odb" ? await WSP.fetchShortDesc(reg.env.wsp, uiCtx, "/~transient/" + wspFrom.code + srcFrom.srcUri) : srcFrom;
		}
		return this.transientCache;
	}

	canCopy(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): boolean {
		//return false; //todo
		//Si on cherche des items (refExtItemAcceptable) on restreint.
		if (!wspFrom.reg.hasPerm("read.node.copyFrom.interWsp")) return false;
		if (!wspFrom.reg.hasPermission("write.node.duplicate", srcFrom.srcRoles, reg.env.universe.auth.currentUser.isSuperAdmin, srcFrom.srcRi))
			return false;
		// La cible est inconnue à ce stade => cette perm 'write.node.copyTo' ne peut etre vérifiée ici
		//if(!reg.hasPerm(["write.node.copyTo"])) return false;// évalué par approximation sur l'atelier
		return options.refExtItemAcceptable ? ITEM.getSrcUriType(srcFrom.srcUri) === "item" : true;
	}

	canPrx(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): boolean {
		return false; //todo
		//return reg.env.wsp.isPrxItem && ITEM.getSrcUriType(srcFrom.srcUri) === "item";
	}

	canExt(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): boolean {
		return options.refExtItemAcceptable && wspFrom.isPublic && reg.env.wsp.isExtItem;
	}

	// async isTargetCompat(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions & OImportItemOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<boolean> {
	// 	if (options.sgnPattern) {
	// 		//Note : en FS, l'eval de compat est imparfaite, on prend l'itModel de l'autre atelier.
	// 		const transient = await this.getTransient(wspFrom, srcFrom, options, reg, uiCtx);
	// 		if (!options.sgnPattern.test(transient.itModel)) {
	// 			POPUP.showNotifInfo("Cet item n'est pas autorisé dans ce contexte d'usage.", uiCtx);
	// 			return false;
	// 		}
	// 	}
	// 	return true;
	// }

	async doPrx(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		const transient = await this.getTransient(wspFrom, srcFrom, options, reg, uiCtx);
		//console.log("transient:::", transient);
		if (transient.srcId) {
			//Proxy déjà existant.
			if (transient.srcSt === ESrcSt.none) {
				//Mais en corbeille, on va proposer de le restaurer
				if (await POPUP.confirm("Une réplique de cet item étranger existe déjà dans cet atelier, mais est en corbeille. Voulez-vous la restaurer ?", uiCtx, {okLbl: "Restaurer"})) {
					await WSP.srcRestore(reg.env.wsp, uiCtx, [transient.srcId]);
				} else return null;
			}
			if (options.uriParentFrozen) {
				if (ITEM.getSrcUriType(transient.srcUri) === "item" && ITEM.isAirItem(transient.srcUri)) {
					//Le prx est en airItem, on propose de l'ancrer à options.defaultUriParent.
					const ct = new SrcNewCode().initialize({
						reg: reg,
						parentFolder: options.defaultUriParent,
						defaultCode: SRC.extractLeafFromUri(transient.srcUri),
						targetSrcUriType: ITEM.getSrcUriType(transient.srcUri),
						allowReplace: options.allowReplace,
						inputLabel: "Une réplique de cet item étranger existe déjà sous forme d'un item flottant. Voulez-vous l'ancrer dans cet espace ?",
						doBtnLabel: (state, canReplace) => (state === 'used' && canReplace) ? "Remplacer le contenu existant" : "Ancrer",
					});
					const newCode = await POPUP.showDialog<string>(ct, uiCtx, {titleBar: {barLabel: {label: "Ancrer la réplique de cet item étranger..."}}}).onNextClose();
					if (newCode) {
						const newUri = SRC.addLeafToUri(options.defaultUriParent, newCode);
						await WSP.srcMove(reg.env.wsp, uiCtx, transient.srcId, newUri);
						return WSP.fetchShortDesc(reg.env.wsp, uiCtx, newUri);
					}
					return null;
				} else {
					//Le proxy est déjà ancré dans un espace
					//XXX proposer déplacement dans options.defaultUriParent avec un SrcNewCode ?
					const uri = ITEM.getItemUriLabel(transient.srcUri);
					POPUP.showNotifInfo(`Une réplique de cet item étranger existe déjà : ${uri}`, uiCtx);
					return transient; //on le retourne simplement (pour sélection auto), même si pas dans options.defaultUriParent.
				}
			}
			return transient; //prx dispo
		}
		//On va créer le proxy
		if (options.uriParentFrozen) {
			//Et l'ancrer
			const uri = ITEM.extractSpaceLabel(options.defaultUriParent);
			const ct = new SrcNewCode().initialize({
				reg: reg,
				parentFolder: options.defaultUriParent,
				defaultCode: SRC.extractLeafFromUri(transient.srcUri),
				targetSrcUriType: ITEM.getSrcUriType(transient.srcUri),
				inputLabel: `Code de cette réplique dans l'espace : ${uri}`,
				doBtnLabel: "Créer"
			});
			const newCode = await POPUP.showDialog<string>(ct, uiCtx, {titleBar: {barLabel: {label: "Créer la réplique de cet item étranger..."}}}).onNextClose();
			if (newCode) {
				const srcId = await WSP.fetchSrcId(reg.env.wsp, uiCtx, transient.srcUri, true);
				const newUri = SRC.addLeafToUri(options.defaultUriParent, newCode);
				await WSP.srcMove(reg.env.wsp, uiCtx, srcId, newUri);
				return WSP.fetchShortDesc(reg.env.wsp, uiCtx, newUri);
			}
		} else if (await POPUP.confirm("Créer une réplique de cet item étranger dans cet atelier ?", uiCtx)) {
			const srcId = await WSP.fetchSrcId(reg.env.wsp, uiCtx, transient.srcUri, true);
			return WSP.fetchShortDesc(reg.env.wsp, uiCtx, srcId);
		}
		return null;
	}

	async doCopy(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		const parentUri = options.defaultUriParent || SRC.URI_ROOT;
		const uri = options.defaultUriParent || "[Racine de l'atelier]";
		const ct = new SrcNewCode().initialize({
			reg: reg,
			parentFolder: parentUri, //Si options.defaultUriParent==null select espace avant ?
			defaultCode: SRC.extractLeafFromUri(srcFrom.srcUri),
			targetSrcUriType: ITEM.getSrcUriType(srcFrom.srcUri),
			inputLabel: `Code dans : ${uri}`,
			doBtnLabel: "Dupliquer"
		});
		const newCode = await POPUP.showDialog<string>(ct, uiCtx, {titleBar: {barLabel: {label: "Dupliquer ce contenu étranger..."}}}).onNextClose();
		if (newCode) {
			const targetWsp = reg.env.wsp;
			if (!targetWsp.isAvailable) await targetWsp.waitForAvailable(uiCtx);
			const targetUri = SRC.addLeafToUri(parentUri, newCode);
			await reg.env.universe.config.wsps.exportUrl.fetchVoid(IO.qs("cdaction", "Copy",
				"param", wspFrom.code, "refUris", srcFrom.srcUri,
				"wspTarget", targetWsp.code, "srcRefTarget", targetUri), {method: "POST"});
			return WSP.fetchShortDesc(targetWsp, uiCtx, targetUri);
		}
		return null;
	}
}